import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { forgotPassword, verifyOtp, resetPassword, resendOtp } from '../redux/slice/auth.slice';
import { Mail, ArrowRight, ArrowLeft, Key, Lock, RefreshCw, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);

    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
    const [otp, setOtp] = useState(['', '', '', '']);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [timer, setTimer] = useState(60);

    // Formik for Step 1: Email
    const emailFormik = useFormik({
        initialValues: {
            email: ''
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Invalid email address').required('Email is required')
        }),
        onSubmit: async (values) => {
            const result = await dispatch(forgotPassword({ email: values.email }));
            if (forgotPassword.fulfilled.match(result)) {
                toast.success("OTP sent to your email");
                setStep(2);
            } else {
                toast.error(result.payload?.message || "Failed to send OTP");
            }
        }
    });

    // Formik for Step 3: Password Reset
    const passwordFormik = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: ''
        },
        validationSchema: Yup.object({
            newPassword: Yup.string()
                .min(6, 'Password must be at least 6 characters')
                .required('New Password is required'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
                .required('Confirm Password is required')
        }),
        onSubmit: async (values) => {
            const result = await dispatch(resetPassword({ email: emailFormik.values.email, password: values.newPassword }));
            if (resetPassword.fulfilled.match(result)) {
                toast.success("Password reset successfully. Please login.");
                navigate('/login');
            } else {
                toast.error(result.payload?.message || "Failed to reset password");
            }
        }
    });

    useEffect(() => {
        let interval;
        if (step === 2 && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return;
        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);

        if (element.nextSibling && element.value) {
            element.nextSibling.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text");
        const pastedOtp = pastedData.replace(/\D/g, '').slice(0, 4);

        if (pastedOtp) {
            const newOtp = [...otp];
            for (let i = 0; i < pastedOtp.length; i++) {
                newOtp[i] = pastedOtp[i];
            }
            setOtp(newOtp);

            // Focus the last filled input or the appropriate input
            const focusIndex = Math.min(pastedOtp.length - 1, 3);
            const focusInput = document.getElementById(`otp-input-${focusIndex}`);
            if (focusInput) focusInput.focus();
        }
    };

    const handleOtpVerify = async (e) => {
        e.preventDefault();
        const otpValue = otp.join('');
        if (otpValue.length !== 4) {
            toast.error("Please enter a valid 4-digit OTP");
            return;
        }

        const result = await dispatch(verifyOtp({ email: emailFormik.values.email, otp: otpValue }));
        if (verifyOtp.fulfilled.match(result)) {
            toast.success("OTP verified successfully");
            setStep(3);
        } else {
            toast.error(result.payload?.message || "Invalid OTP");
        }
    };

    const handleResendOtp = async () => {
        const result = await dispatch(resendOtp({ email: emailFormik.values.email }));
        if (resendOtp.fulfilled.match(result)) {
            toast.success("OTP resent successfully");
            setTimer(60);
        } else {
            toast.error(result.payload?.message || "Failed to resend OTP");
        }
    };

    return (
        <div className="min-h-screen bg-surface flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/5 blur-[100px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-900/5 blur-[100px] rounded-full pointer-events-none" />

            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative z-10 animate-fade-in">
                <div className="p-8 sm:p-10">
                    {/* Header */}
                    <div className="text-center mb-10">
                        <button
                            onClick={() => step === 1 ? navigate('/login') : setStep(step - 1)}
                            className="absolute top-8 left-8 p-2 text-slate-400 hover:text-primary hover:bg-slate-50 rounded-full transition-all"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>

                        <div className="w-12 h-12 bg-primary/5 text-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
                            {step === 1 && <Mail className="w-6 h-6" />}
                            {step === 2 && <Key className="w-6 h-6" />}
                            {step === 3 && <Lock className="w-6 h-6" />}
                        </div>

                        <h1 className="text-2xl font-serif font-bold text-primary mb-2">
                            {step === 1 && "Forgot Password?"}
                            {step === 2 && "Enter OTP"}
                            {step === 3 && "Reset Password"}
                        </h1>
                        <p className="text-slate-500 text-sm px-4">
                            {step === 1 && "Enter your email address and we'll send you an OTP to reset your password."}
                            {step === 2 && `We've sent a 4-digit code to ${emailFormik.values.email}`}
                            {step === 3 && "Create a new strong password for your account."}
                        </p>
                    </div>

                    {/* Step 1: Email */}
                    {step === 1 && (
                        <form onSubmit={emailFormik.handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-primary mb-2">Email Address</label>
                                <div className="relative group">
                                    <Mail className={`absolute top-3.5 left-4 w-5 h-5 transition-colors ${emailFormik.touched.email && emailFormik.errors.email ? 'text-red-500' : 'text-slate-400 group-focus-within:text-secondary'}`} />
                                    <input
                                        type="email"
                                        name="email"
                                        value={emailFormik.values.email}
                                        onChange={emailFormik.handleChange}
                                        onBlur={emailFormik.handleBlur}
                                        className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-4 transition-all outline-none bg-slate-50/50 text-gray-700 font-medium placeholder:font-normal ${emailFormik.touched.email && emailFormik.errors.email ? 'border-red-500 focus:ring-red-100 focus:border-red-500' : 'border-gray-200 focus:ring-secondary/10 focus:border-secondary'}`}
                                        placeholder="Enter your email"
                                        autoFocus
                                    />
                                </div>
                                {emailFormik.touched.email && emailFormik.errors.email && (
                                    <p className="text-red-500 text-xs mt-1 ml-1">{emailFormik.errors.email}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3.5 bg-primary text-white font-bold rounded-xl shadow-lg shadow-blue-900/10 hover:bg-tertiary transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        Send OTP <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    {/* Step 2: OTP */}
                    {step === 2 && (
                        <form onSubmit={handleOtpVerify} className="space-y-8">
                            <div className="flex justify-center gap-3">
                                {otp.map((data, index) => (
                                    <input
                                        key={index}
                                        id={`otp-input-${index}`}
                                        type="text"
                                        maxLength="1"
                                        value={data}
                                        onChange={(e) => handleOtpChange(e.target, index)}
                                        onPaste={handlePaste}
                                        onKeyDown={(e) => {
                                            if (e.key === "Backspace" && !otp[index] && index > 0) {
                                                e.target.previousSibling.focus();
                                            }
                                        }}
                                        onFocus={(e) => e.target.select()}
                                        className="w-14 h-14 border border-gray-200 rounded-xl text-center text-2xl font-bold text-primary focus:border-secondary focus:ring-4 focus:ring-secondary/10 outline-none transition-all bg-slate-50/50"
                                    />
                                ))}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3.5 bg-primary text-white font-bold rounded-xl shadow-lg shadow-blue-900/10 hover:bg-tertiary transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        Verify OTP <CheckCircle className="w-4 h-4" />
                                    </>
                                )}
                            </button>

                            <div className="text-center">
                                {timer > 0 ? (
                                    <p className="text-sm text-slate-400 font-medium">
                                        Resend OTP in <span className="text-primary">{timer}s</span>
                                    </p>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={loading}
                                        className="text-sm font-medium text-slate-500 hover:text-secondary flex items-center justify-center gap-1.5 mx-auto transition-colors disabled:opacity-50"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5" /> Resend OTP
                                    </button>
                                )}
                            </div>
                        </form>
                    )}

                    {/* Step 3: New Password */}
                    {step === 3 && (
                        <form onSubmit={passwordFormik.handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-primary mb-2">New Password</label>
                                <div className="relative group">
                                    <Lock className={`absolute top-3.5 left-4 w-5 h-5 transition-colors ${passwordFormik.touched.newPassword && passwordFormik.errors.newPassword ? 'text-red-500' : 'text-slate-400 group-focus-within:text-secondary'}`} />
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        name="newPassword"
                                        value={passwordFormik.values.newPassword}
                                        onChange={passwordFormik.handleChange}
                                        onBlur={passwordFormik.handleBlur}
                                        className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:ring-4 transition-all outline-none bg-slate-50/50 text-gray-700 font-medium placeholder:font-normal ${passwordFormik.touched.newPassword && passwordFormik.errors.newPassword ? 'border-red-500 focus:ring-red-100 focus:border-red-500' : 'border-gray-200 focus:ring-secondary/10 focus:border-secondary'}`}
                                        placeholder="Enter new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute top-3.5 right-4 text-slate-400 hover:text-secondary transition-colors focus:outline-none"
                                    >
                                        {showNewPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                    </button>
                                </div>
                                {passwordFormik.touched.newPassword && passwordFormik.errors.newPassword && (
                                    <p className="text-red-500 text-xs mt-1 ml-1">{passwordFormik.errors.newPassword}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-primary mb-2">Confirm Password</label>
                                <div className="relative group">
                                    <Lock className={`absolute top-3.5 left-4 w-5 h-5 transition-colors ${passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword ? 'text-red-500' : 'text-slate-400 group-focus-within:text-secondary'}`} />
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={passwordFormik.values.confirmPassword}
                                        onChange={passwordFormik.handleChange}
                                        onBlur={passwordFormik.handleBlur}
                                        className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:ring-4 transition-all outline-none bg-slate-50/50 text-gray-700 font-medium placeholder:font-normal ${passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword ? 'border-red-500 focus:ring-red-100 focus:border-red-500' : 'border-gray-200 focus:ring-secondary/10 focus:border-secondary'}`}
                                        placeholder="Confirm new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute top-3.5 right-4 text-slate-400 hover:text-secondary transition-colors focus:outline-none"
                                    >
                                        {showConfirmPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                    </button>
                                </div>
                                {passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword && (
                                    <p className="text-red-500 text-xs mt-1 ml-1">{passwordFormik.errors.confirmPassword}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3.5 bg-primary text-white font-bold rounded-xl shadow-lg shadow-blue-900/10 hover:bg-tertiary transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        Reset Password <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
